import React, { useEffect, useState } from "react";
import { Nav, Image } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import "../../../assets/css/Sidebar.css";
import { useTranslate } from "../../../hooks/LanguageProvider";
import Logout from "../../Logout/Logout";
import usePermissions from "../../../hooks/usePermissions";
import { useUser } from "../../../context/UserContext";
import { useAuth } from "../../../context/AuthContext";
import { menuItems1 } from "../../../config/menu/menu1";
import { menuItems2 } from "../../../config/menu/menu2";
import { menuItems3 } from "../../../config/menu/menu3";
import { menuItems4 } from "../../../config/menu/menu4";
import type { MenuItem } from "../../../config/menu/menu.types";

interface SidebarProps {
  onSidebarStateChange: (isOpen: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onSidebarStateChange }) => {
  const { translate } = useTranslate();
  const [isOpen, setIsOpen] = useState("");
  const [activeLogo, setActiveLogo] = useState("header-logo-hide");
  const [activeMenuText, setActiveMenuText] = useState("iq-menu-span-hide");
  const [menuButtonSidebar, setMenuButtonSidebar] = useState(
    "iq-menu-bt-sidebar-hide",
  );
  const [sidebar, setSidebar] = useState("sidebar-close");
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const { pathImg } = useUser();

  const navigate = useNavigate();
  const { logout } = useAuth();

  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window.matchMedia("(max-width: 1299px)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1299px)");

    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsSmallScreen(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => {
        mediaQuery.removeEventListener("change", handleMediaQueryChange);
      };
    }

    mediaQuery.addListener(handleMediaQueryChange);
    return () => {
      mediaQuery.removeListener(handleMediaQueryChange);
    };
  }, []);

  const roleId = localStorage.getItem("id_role");
  const numericRoleId = Number(roleId || 0);
  const { userPermissions } = usePermissions(roleId);

  const hasPermission = (permissionId: number): boolean =>
    userPermissions.some(
      (permission) => permission.id_permission === permissionId,
    );

  const checkAccess = (item: MenuItem): boolean => {
    const hasRole =
      !item.allowedRoles || item.allowedRoles.includes(numericRoleId);

    if (item.id === 11 && (numericRoleId === 2 || numericRoleId === 3)) {
      return false;
    }

    return hasPermission(item.permissionId) && hasRole;
  };

  const handleSubmenuClick = (submenuId: string): void => {
    if (openSubmenus.includes(submenuId)) {
      setOpenSubmenus([]);
    } else {
      setOpenSubmenus([submenuId]);
    }
  };

  const handleSetIsOpen = () => {
    const nextIsOpen = sidebar === "sidebar-close";

    isOpen === "open" ? setIsOpen("") : setIsOpen("open");

    sidebar === "sidebar-close"
      ? setSidebar("sidebar-open")
      : setSidebar("sidebar-close");

    activeLogo === "header-logo-show"
      ? setActiveLogo("header-logo-hide")
      : setActiveLogo("header-logo-show");

    activeMenuText === "iq-menu-span-hide"
      ? setActiveMenuText("")
      : setActiveMenuText("iq-menu-span-hide");

    menuButtonSidebar === "iq-menu-bt-sidebar-show"
      ? setMenuButtonSidebar("iq-menu-bt-sidebar-hide")
      : setMenuButtonSidebar("iq-menu-bt-sidebar-show");

    onSidebarStateChange(nextIsOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erreur pendant la déconnexion :", error);
    }
  };

  const handleMouseEnter = () => {
    if (isSmallScreen) return;

    setIsOpen("open");
    setSidebar("sidebar-open");
    setActiveLogo("header-logo-show");
    setActiveMenuText("");
    setMenuButtonSidebar("iq-menu-bt-sidebar-show");
    onSidebarStateChange(true);
  };

  const handleMouseLeave = () => {
    if (isSmallScreen) return;

    setIsOpen("");
    setSidebar("sidebar-close");
    setActiveLogo("header-logo-hide");
    setActiveMenuText("iq-menu-span-hide");
    setMenuButtonSidebar("iq-menu-bt-sidebar-hide");
    onSidebarStateChange(false);
  };

  const handleMenuNavigation = () => {
    setOpenSubmenus([]);

    if (isSmallScreen) {
      handleSetIsOpen();
    }
  };

  const menuSections = [menuItems1, menuItems2, menuItems3, menuItems4];

  return (
    <div
      style={{ zIndex: 10015 }}
      className={`iq-sidebar sidebar-default ${sidebar}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="iq-sidebar-logo d-flex align-items-center justify-content-between">
        <Nav.Link to="/" className={`header-logo ${activeLogo}`} as={NavLink}>
          <Image
            className="img-fluid rounded-normal light-logo"
            style={{
              width: "136px",
              height: "auto",
              maxHeight: "50px",
              objectFit: "contain",
            }}
            src={"asset/images/logo.png"}
          />
        </Nav.Link>

        <div
          className={`iq-menu-bt-sidebar ml-0 ${menuButtonSidebar}`}
          onClick={handleSetIsOpen}
        >
          <i className={`las la-bars wrapper-menu ${isOpen}`}></i>
        </div>
      </div>

      <div
        className="data-scrollbar"
        data-scroll="1"
        data-scrollbar="true"
        style={{ overflow: "hidden", outline: "none" }}
      >
        <div className="scroll-content">
          <nav className="iq-sidebar-menu">
            {menuSections.map((menuSection, index) => (
              <ul
                key={`menu-section-${index}`}
                id="iq-sidebar-toggle"
                className="iq-menu"
              >
                {index > 0 && (
                  <li className="divider" style={{ margin: "0 0 5px" }}></li>
                )}

                {menuSection.map((menuItem) =>
                  checkAccess(menuItem) ? (
                    menuItem.divider ? (
                      <li key={menuItem.id} className=""></li>
                    ) : (
                      <li key={menuItem.id}>
                        {menuItem.to ? (
                          <Nav.Link
                            to={menuItem.to}
                            className="svg-icon"
                            as={NavLink}
                            onClick={handleMenuNavigation}
                          >
                            <i className={menuItem.icon} />
                            <span className={`ml-3 ${activeMenuText}`}>
                              {translate(menuItem.label)}
                            </span>
                          </Nav.Link>
                        ) : (
                          <Nav.Link
                            onClick={() => handleSubmenuClick(menuItem.label)}
                          >
                            <i className={menuItem.icon} />
                            <span className={`ml-3 ${activeMenuText}`}>
                              {translate(menuItem.label)}
                            </span>
                            <svg
                              style={{ minWidth: "fit-content" }}
                              className="svg-icon iq-arrow-right arrow-active"
                              width="20"
                              height="20"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="10 15 15 20 20 15"></polyline>
                              <path d="M4 4h7a4 4 0 0 1 4 4v12"></path>
                            </svg>
                          </Nav.Link>
                        )}

                        {menuItem.subItems && (
                          <ul
                            className={`iq-submenu ${
                              openSubmenus.includes(menuItem.label)
                                ? "submenu-enter-active"
                                : "submenu-enter"
                            }`}
                          >
                            {menuItem.subItems.map(
                              (subItem) =>
                                hasPermission(subItem.permissionId) && (
                                  <Nav.Link
                                    key={subItem.id}
                                    to={subItem.to || "/"}
                                    className="svg-icon"
                                    as={NavLink}
                                    onClick={handleMenuNavigation}
                                  >
                                    <i className={subItem.icon} />
                                    <span>{translate(subItem.label)}</span>
                                  </Nav.Link>
                                ),
                            )}
                          </ul>
                        )}
                      </li>
                    )
                  ) : null,
                )}
              </ul>
            ))}
          </nav>

          <div
            className="position-relative sidebar-bottom"
            style={{ padding: "0!important", margin: 0 }}
          >
            <nav className="iq-sidebar-menu">
              <ul className="iq-menu" style={{ padding: 0 }}>
                <li className="divider" style={{ margin: "0 0 5px" }}></li>
                <Logout
                  onLogout={handleLogout}
                  activeMenu={activeMenuText}
                  title={translate("Logout")}
                  margin="ml-3"
                />
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
